import type {
  CompanySize,
  MaterialType,
  NetworkContactListSort,
  Notice,
  Tier,
  UserLocale,
} from '@mnd-frontend/graphql-schema';
import type { ReportsSharedTrackingEvent } from '@mnd-frontend/reports-shared';
import { type IdentifyEvent, initTracking, type NativePageEvent } from '@mnd-frontend/tracking';
import type {
  BetaFeaturesFragment,
  CopilotFeaturesFragment,
  PitchToFragmentFragment,
} from '../generated/types';
import type { Selected } from '../modules/contacts/pages/ContactList/ContactsInactivePage/hooks';
import type { VideoActionEvent } from '../modules/dashboard/components/OnboardingVideoCourse/types';
import type { SuggestionType } from '../modules/publish/components/EditMaterials/SidePanel/Body/SocialMedia';
import type { TemplateTypes } from '../modules/publish/components/GenerateMaterialsPage/types';

import type { KindsOfPrAssistance } from '../modules/reports/coverageReport/edit/ReportContext';
import type { UrlRegularPublishMaterialTypes } from '../routes';

interface TraitsLessEvent {
  event:
    | 'Account settings: Clicked Invite users'
    | 'Account settings: Clicked removed invitation'
    | 'Account settings: Clicked remove users'
    | 'Analyze: Clicked Clicked in Emails to contacts'
    | 'Analyze: Clicked Clicked in Emails to followers'
    | 'Analyze: Clicked Delivered in Emails to contacts'
    | 'Analyze: Clicked Delivered in Emails to followers'
    | 'Analyze: Clicked Delivered in Emails to subscribers'
    | 'Analyze: Clicked Opened in Emails to contacts'
    | 'Analyze: Clicked Opened in Emails to followers'
    | 'Analyze: Newsroom Click Copy Link'
    | 'Analyze: Overview: Media coverage: Clicked "Only for Analyze" pill'
    | 'Analyze: Overview: Media coverage: Clicked "Show more"'
    | 'Analyze: Overview: Media coverage: Clicked "Upgrade" button'
    | 'Analyze: Your content: Email reach: Followers: Show'
    | 'Analyze: Your content: Email reach: Subscribers: Show'
    | 'Analyze: Your content: Email reach: Your contacts: Show'
    | 'Analyze: Your content: Email reach: Send Exclusives: Show'
    | 'Analyze: Your content: Views: Email reach summary: Clicked CTA'
    | 'Audience Builder: Clicked Delete Saved Search'
    | 'Audience Targeting: Clicked "Done" in "Review and add"'
    | 'Audience Targeting: Start manually input email'
    | 'Automated Topics: Seen Feature Modal'
    | 'Campaign: Limit Free - Submit Form'
    | 'Dashboard: Clicked Distribute button in Draft widget'
    | 'Dashboard: Clicked draft link in Draft widget'
    | 'Dashboard: Clicked Edit button in Draft widget'
    | 'Dashboard: Clicked Go Live in Create Newsroom Widget'
    | 'Dashboard: Clicked Link in Onboarding'
    | 'Dashboard: Confirmed go live with newsroom'
    | 'Dashboard: Seen Create Newsroom Widget'
    | 'Dashboard: Seen Draft Widget'
    | 'Dashboard: Seen Help&Support Widget'
    | 'Dashboard: Seen Write First Pressrelease Widget'
    | 'Dashboard: TopStories: Clicked CTA'
    | 'Dashboard: Seen CoPilot Widget'
    | 'Dashboard: Clicked CTA in Copilot Widget'
    | 'Dashboard: Clicked Read moore in Copilot Widget'
    | 'Dashboard: Seen Brand Report Banner H1 2024'
    | 'Dashboard: Clicked CTA in Brand Report Banner H1 2024'
    | 'Dashboard: Clicked Dismiss in Brand Report Banner H1 2024'
    | 'Find Contacts: Show limit reached modal'
    | 'Find Contacts: Clicked import contacts'
    | 'Find Contacts: Clicked on toggle to show articles of contact'
    | 'Help Panel: Clicked Start'
    | 'Latest Story: Clicked Analyze'
    | 'Latest Story: Clicked Distribute'
    | 'Latest Story: Clicked Edit'
    | 'Latest Story: Clicked View'
    | 'Limit: Clicked on "Read More" in Cap in Play Modal'
    | 'Limit: Clicked on "Read More" in Heads up Modal'
    | 'Limit: Clicked on Out of Publications CTA'
    | 'Monitor: Chart Preview Modal - Clicked See All'
    | 'Monitor: Clicked Create New Clipboard From Media Feed'
    | 'Monitor: Clicked Create New Clipboard'
    | 'Monitor: Clicked Create New Search Profile'
    | 'Monitor: Clicked Edit Clipboard'
    | 'Monitor: Clicked Export As Png'
    | 'Monitor: Clicked on Geographical Breakdown reach number'
    | 'Monitor: Clicked on Geographical Impact hit number'
    | 'Monitor: Clicked Save New Clipboard From Media Feed'
    | 'Monitor: Clicked Save New Clipboard'
    | 'Monitor: Clicked Send Clipboard by Email'
    | 'Monitor: Clicked Share Clipboard by RSS feed'
    | 'Monitor: Clicked Shareable Link'
    | 'Monitor: Removed Mention from Media Feed'
    | 'Monitor: Seen Prime Clipboards Feature Modal'
    | 'Monitor: Selected profile in Media Feed'
    | 'Monitor: Selected region in Media Feed'
    | 'Monitor: Selected source in Media Feed'
    | 'Monitor: Selected timespan in Media Feed'
    | 'Monitor: Started Search Profile Creation'
    | 'Monitor: Removed Suggested Competitor in Smart Search Onboarding'
    | 'Monitor: Added Competitor in Smart Search Onboarding'
    | 'Onboarding Video Course: dismiss'
    | 'Onboarding Video Course: course completed'
    | 'Payment: Clicked Buy Basic+'
    | 'Payment: Clicked Cancel Subscription'
    | 'Payment: Clicked Confirm Switch to Free'
    | 'Payment: Clicked Contact Us'
    | 'Payment: Clicked Pay Now'
    | 'Payment: Clicked Stay On My Current Plan'
    | 'Payment: Confirm cancellation'
    | 'Payment: View Account'
    | 'Payment: View Cancel Subscription'
    | 'Payment: View Update Billing Information'
    | 'PR Impact: Clicked Shareable Link'
    | 'PR Impact: View Media Coverage'
    | 'Press Release: Clicked Cancel Backdate/Publish/Schedule'
    | 'Press Release: Clicked Continue to Confirm'
    | 'Press Release: Clicked Continue to Distribution'
    | 'Press Release: Clicked Create New'
    | 'Press Release: Clicked Edit'
    | 'Press Release: Clicked Preview'
    | 'Press Release: Clicked Update'
    | 'Publish: Clicked on Premium Feature Modal CTA'
    | 'Publish: Clicked on Radio Button Publish'
    | 'Publish: Clicked on Radio Button Schedule'
    | 'Publish: Clicked on Checkbox Expire'
    | 'Publish: Seen Sidebar'
    | 'Publish: Seen Sidebar Feature Modal'
    | 'Publish: Seen Sidebar Explanation Feature Popup'
    | 'Publish: Empty Opened Emails Rate Graph'
    | 'Settings: Activate Hosted Newsroom Settings'
    | 'Social Media: Seen Beta Modal'
    | 'Social Media: View Page'
    | 'Social Media: Clicked Create Post CTA'
    | 'Coverage Reports: Create Report'
    | 'Coverage Reports: Clicked Edit'
    | 'Coverage Reports: Clicked Delete'
    | 'Coverage Reports: Clicked Slideshow'
    | 'Coverage Reports: Clicked Start Trial'
    | 'Coverage Reports: Clicked Copy Share Link'
    | 'Coverage Reports: Clicked Copy Share Slideshow Link'
    | 'Coverage Reports: Clicked Export To PDF'
    | 'Coverage Reports: Seen in app message'
    | 'Coverage Reports: Clicked Q1 Report made for you'
    | 'Coverage Reports: Onboarding Blue icon click'
    | 'Coverage Reports: Clicked Preview'
    | 'Reports: Clicked Start Trial'
    | 'Reports: Clicked Contact Me'
    | 'Reports: Viewed Upsell Page'
    | 'Brand Reports: Viewed Create Page'
    | 'Brand Reports: Viewed Create Express Page'
    | 'Brand Reports: Clicked Create in Creating Report Express Page'
    | 'Brand Reports: Clicked Preview'
    | 'Brand Reports: Clicked Edit'
    | 'Brand Reports: Clicked Preview Demo'
    | 'Brand Reports: Clicked Delete'
    | 'Brand Reports: Clicked Slideshow'
    | 'Brand Reports: Clicked Copy Share Link'
    | 'Brand Reports: Clicked Export To PDF'
    | 'Brand Reports: Clicked Download PDF'
    | 'Brand Reports: Clicked Use CPC in Ad value settings'
    | 'Brand Reports: Clicked Use CPM in Ad value settings'
    | 'Brand Reports: Changed CPC in Ad value web settings'
    | 'Brand Reports: Changed CPM in Ad value web settings'
    | 'Brand Reports: Changed Twitter CPM in Ad value settings'
    | 'Brand Reports: Changed Facebook CPM in Ad value settings'
    | 'Brand Reports: Changed Instagram CPM in Ad value settings'
    | 'Brand Reports: Added Reach Highlights'
    | 'Brand Reports: Removed Reach Highlights'
    | 'AiWriter: Clicked on AI Credits button in distribution summary'
    | 'AiWriter: Clicked on AI Credits button in publish confirmation modal'
    | 'AiWriter: Seen Feature Modal'
    | 'AiWriter: Seen New Draft Feature Modal'
    | 'Contacts: Opened Automatically Recommended Contacts List'
    | 'Contacts: Seen Matches Journalists List'
    | 'Preview page: Clicked share button'
    | 'Copilot Intro Modal: Seen modal'
    | 'Copilot Intro Modal: Clicked Try It Now'
    | 'Copilot Intro Modal: Clicked Request Access'
    | 'Copilot Intro Modal: Clicked Close'
    | 'Copilot Intro Modal Mini: Confirmed'
    | 'Copilot Url Validation: Seen modal'
    | 'Copilot Url Validation: Clicked Validate Url'
    | 'Copilot Url Validation: Clicked Remind Me Later'
    | 'Copilot Url Validation: Clicked Read More'
    | 'ToneOfVoice: Modal Open'
    | 'ToneOfVoice: Changed Formality'
    | 'ToneOfVoice: Changed Seriousity'
    | 'ToneOfVoice: Changed Politeness'
    | 'ToneOfVoice: Changed Matter-of-fact'
    | 'ToneOfVoice: Modal Submit'
    | 'ToneOfVoice: Modal Cancel'
    | 'ToneOfVoice: Modal Reset'
    | 'Media relations: View Page'
    | 'Media relations: Top journalist - Clicked on add'
    | 'Media relations: Top journalist - Clicked on view journalist'
    | 'Media relations: Top journalist - Clicked on next slide'
    | 'Media relations: Top journalist - Clicked on previous slide'
    | 'Media relations: Contact card - Clicked on add'
    | 'Media relations: Contact card - Clicked on pitch'
    | 'Media relations: Contact card - Clicked on article'
    | 'Media relations: Contact card - Clicked on grouped article'
    | 'Media relations: Search list - Clicked on view journalist'
    | 'Media relations: Search list - Clicked on select'
    | 'Media relations: Search list - Clicked on select all'
    | 'Media relations: Search list - Clicked on add journalist in row'
    | 'Media relations: Search outlet list - Clicked on select'
    | 'Media relations: Search outlet list - Clicked on select all'
    | 'Media relations: Search outlet list - Clicked on view media outlet'
    | 'Media relations: Search outlet list - Clicked on add media outlet in row'
    | 'User settings: Clicked Save button'
    | 'User settings: Clicked Disable new navigation'
    | 'User settings: Clicked Enable new navigation'
    | 'Interaction: Clicked Disable new navigation button'
    | 'EmailVerification: View Page'
    | 'Onboarding intro: Navigate to Brand';

  traits?: undefined;
}

interface OnboardingIntroNavigateToCompetitors {
  event: 'Onboarding intro: Navigate to competitors';
  traits: {
    brandName: string;
    brandUrl: string;
    brandNameChangedByUser: boolean;
    brandUrlChangedByUser: boolean;
  };
}

interface OnboardingIntroDone {
  event: 'Onboarding intro: Done';
  traits: {
    competitors: {
      brandName: string;
      brandUrl: string;
      manuallyAdded: boolean;
      selected: boolean;
    }[];
  };
}

interface MediaRelationsSearchModeToggleEvent {
  event: 'Media relations: Toggle Search Mode';
  traits: {
    searchMode: 'journalist' | 'outlet';
  };
}

interface MediaRelationsPaginationEvent {
  event:
    | 'Media relations: Pagination - Clicked on next page'
    | 'Media relations: Pagination - Clicked on previous page'
    | 'Media relations: Pagination outlet - Clicked on next page'
    | 'Media relations: Pagination outlet - Clicked on previous page';
  traits: {
    currentPage: number;
    paginateTo: number;
    totalPages: number;
  };
}

interface MediaRelationsListSortEvent {
  event:
    | 'Media relations: Search list - Sort by name'
    | 'Media relations: Search list - Sort by match'
    | 'Media relations: Search list - Sort by readers'
    | 'Media relations: Search list - Sort by outlet';
  traits: {
    sortBy: 'asc' | 'desc';
  };
}

interface MediaRelationsOutletListSortEvent {
  event:
    | 'Media relations: Search outlet list - Sort by name'
    | 'Media relations: Search outlet list - Sort by match'
    | 'Media relations: Search outlet list - Sort by reach'
    | 'Media relations: Search outlet list - Sort by content match';
  traits: {
    sortBy: 'asc' | 'desc';
  };
}

interface MediaRelationsSelectEvent {
  event:
    | 'Media relations: Search list - Clicked on add selected journalists'
    | 'Media relations: Search list - Select journalist';
  traits: {
    selectedJournalistCount: number;
  };
}

interface MediaRelationsSelectOutletEvent {
  event:
    | 'Media relations: Search outlet list - Clicked on add selected media outlets'
    | 'Media relations: Search outlet list - Select media outlet';
  traits: {
    selectedOutletCount: number;
  };
}

interface MediaRelationsImportEvent {
  event:
    | 'Media relations: Import - Clicked on add'
    | 'Media relations: Import - Clicked on create list';
  traits:
    | {
        selectedJournalistCount: number;
        selectedListCount?: number;
        alreadyImportedJournalists: number;
      }
    | {
        selectedOutletsCount: number;
        selectedListCount?: number;
        alreadyImportedOutlets: number;
      };
}

type Option = {
  value: string;
  label: string;
};
interface MediaRelationsFiltersEvent {
  event:
    | 'Media relations: Filters - Clicked on advanced search'
    | 'Media relations: Filters - Clicked on basic search'
    | 'Media relations: Filters - No hits'
    | 'Media relations: Filters - Used filter to search';
  traits: {
    filters: {
      categories?: Option[];
      regions?: Option[];
      mediaTypes?: Option[];
      jobRoles?: Option[];
      geoTargets?: Option[];
      name?: string;
      mediaOutlets?: Option[];
      materialId?: string;
      materialType?: string;
    };
  };
}

interface MediaRelationsOutletFiltersEvent {
  event:
    | 'Media relations: Outlet Filters - Clicked on advanced search'
    | 'Media relations: Outlet Filters - Clicked on basic search'
    | 'Media relations: Outlet Filters - No hits'
    | 'Media relations: Outlet Filters - Used filter to search';
  traits: {
    filters: {
      categories?: Option[];
      regions?: Option[];
      mediaTypes?: Option[];
      outletName?: string;
    };
  };
}

interface TopicsAndRegionsEvent {
  event:
    | 'Settings: Searched for Topics/Regions in Modal'
    | 'Settings: Seen Topics/Regions'
    | 'Settings: Clicked on Remove Topic/Region'
    | 'Settings: Clicked on Add Topic/Region'
    | 'Settings: Closed Topics/Regions Modal'
    | 'Settings: Opened Topics/Regions Modal'
    | 'Publish: Searched for Topics/Regions in Modal'
    | 'Publish: Seen Topics/Regions'
    | 'Publish: Clicked on Remove Topic/Region'
    | 'Publish: Clicked on Add Topic/Region'
    | 'Publish: Closed Topics/Regions Modal'
    | 'Publish: Opened Topics/Regions Modal';
  traits?: {
    name?: 'regions' | 'topics';
    search?: string;
  };
}

interface PublishRecommendedJournalistsEvent {
  event:
    | 'Publish: Clicked Import Recommended Journalist Button'
    | 'Publish: Clicked Recommended Journalist Checkbox'
    | 'Publish: Clicked Select All Recommended Journalists Checkbox'
    | 'Publish: Closed Recommended Journalists Modal'
    | 'Publish: Clicked Open Recommended Journalists Modal Button'
    | 'Publish: Received Recommended Journalists'
    | 'Publish: Received no Recommended Journalists'
    | 'Publish: Seen Recommended Journalists';
  traits?: {
    checked?: boolean;
    selectedJournalistEmail?: string;
    selectedJournalistEmails?: string[];
    recommendedJournalistEmails?: string[];
    mndReach?: boolean;
  };
}

interface CoverageReportOnboardingEvent {
  event:
    | 'Coverage Reports: Onboarding Clicked Next'
    | 'Coverage Reports: Onboarding Clicked Hide Tips';
  traits?: {
    currentStep: 'title' | 'image' | 'metrics' | 'coverage' | 'design' | 'sections' | null;
  };
}

interface CoverageReportPrAssistantTrigger {
  event: 'Coverage Reports: PR Assistant trigger';
  traits?: {
    kind: KindsOfPrAssistance;
    sectionId?: string;
    reportId?: string;
  };
}

interface CoverageReportEditSectionDescriptionEvent {
  event: 'Coverage Reports: Edit Section Description';
  traits: {
    sectionId: string;
    before: string;
    after: string;
  };
}

interface CoverageReportRemoveHighlightEvent {
  event: 'Coverage Reports: Remove Highlight';
  traits: {
    highlightsLeftAfter?: number;
    reportId?: string;
  };
}

interface AiWriterUseCreditsEvent {
  event:
    | 'Publish: Clicked on Unschedule material'
    | 'Publish: Clicked on Delete material'
    | 'Publish: Clicked on Confirm Delete material';
  traits?: {
    creditsConsumed?: number;
    currentCredits?: number;
    creditsAfterEvent?: number;
  };
}

interface AiWriterQuestionsEvent {
  event:
    | 'AiWriter: Clicked on Question'
    | 'AiWriter: Clicked on Improve button'
    | 'AiWriter: Clicked on Answer question button';
  traits: {
    questionsAndAnswers?: {
      question: string;
      answer: string;
      answered: boolean;
    }[];
    question?: string;
    answer?: string;
    textLength?: string;
  };
}

interface AiWriterCreditsPlanType {
  id: string;
  credits: number;
  currency: string;
  price: number;
  pricePerCredit: number;
  priceDiscount: number;
}
interface AiWriterClickedBuyCreditsEvent {
  event:
    | 'AiWriter: Clicked on Buy credits button'
    | 'AiWriter: Clicked on Purchase button in AI Credits modal'
    | 'AiWriter: Clicked on Close button in AI Credits modal';
  traits?: {
    plan: AiWriterCreditsPlanType;
    currentCredits?: number;
    creditsAfterPurchase?: number;
  };
}

interface AiWriterBaseEvent {
  event:
    | 'AiWriter: Visit page'
    | 'AiWriter: Click on Template'
    | 'AiWriter: Click on Generate'
    | 'AiWriter: Click on Regenerate'
    | 'AiWriter: Start'
    | 'AiWriter: Error'
    | 'AiWriter: Error Rate Limit'
    | 'AiWriter: Flagged By Moderation'
    | 'AiWriter: Continue to publish'
    | 'AiWriter: Click on Go back button'
    | 'AiWriter: Results Selector Event'
    | 'AiWriter: Create Draft From Menu';
  traits: {
    template: TemplateTypes;
    materialType: MaterialType;
    storyId?: string;
  };
}

interface AiWriterClickedCreditsEvent {
  event:
    | 'AiWriter: Clicked on Add more credits link'
    | 'AiWriter: Clicked on Close button in AI Credits popover'
    | 'AiWriter: Clicked on AI Credits button in popover';
  traits: {
    template?: TemplateTypes;
    materialType?: MaterialType;
  };
}

interface AiWriterGenerateEvent {
  event: 'AiWriter: Result';
  traits: {
    header: string;
    body: string;
    template: TemplateTypes;
    materialType: MaterialType;
  };
}

interface OpenOnboaringVideoCourseModal {
  event: 'Onboarding Video Course: modal opened';
  traits: {
    referer: 'interaction' | 'url';
  };
}

interface ToolTrainingVideosInteractions {
  event: 'Tool training videos - Interactions';
  traits: {
    action: VideoActionEvent;
    id: string;
    title: string;
    progress?: number;
    duration: number;
    episode: number;
  };
}
interface SocialMediaClickedSuggestedTime {
  event: 'Social Media: Clicked Suggested Time';
  traits: {
    time: string;
    weekday: number;
  };
}
interface SocialMediaCreatedPost {
  event: 'Social Media: Created post';
  traits: {
    scheduledFor?: string;
    images: number;
    embeddedLink: boolean;
    platform: 'linkedin';
  };
}
interface SocialMediaEditedPost {
  event: 'Social Media: Edited post';
  traits: {
    id: string;
    scheduledFor?: string;
    images: number;
    embeddedLink: boolean;
    platform: 'linkedin';
  };
}
interface SocialMediaDeletePost {
  event: 'Social Media: Delete post';
  traits: {
    id: string;
    platform: 'linkedin';
  };
}
interface SocialMediaEditPostOpened {
  event: 'Social Media: Edit post opened';
  traits: {
    id: string;
    platform: 'linkedin';
  };
}

interface AnalyzeYourContentClickCopyLinkEvent {
  event: 'Analyze: Your Content Click Copy Link';
  traits: {
    materialType: string;
  };
}

interface AnalyzeExportEvent {
  event: 'Analyze: Export';
  traits: {
    page: 'Newsroom' | 'Your Content';
  };
}
interface AnalyzeTimeFilterEvent {
  event: 'Analyze: TimeFilter';
  traits: {
    page: 'Overview'; // TODO: Add more for other pages
    filterValue: string;
  };
}
interface AnalyzeMediaCoverageLinkEvent {
  event: 'Analyze: Overview: Media coverage: Clicked article';
  traits: {
    articleUrl: string;
  };
}

interface AudienceBuilderClickedSaveSearchEvent {
  /**
   *  This tracks Advanced Search click
   */
  event: 'Audience Builder: Clicked Save Search';
  traits: {
    searchName: string;
  };
}

interface AudienceBuilderClickedKeywordSearch {
  event: 'Audience Builder: Clicked Keyword Search';
  traits: {
    keyword: string;
  };
}

interface AudienceBuilderClickedSearchEvent {
  event: 'Audience Builder: Clicked Search';
  traits: {
    contactNames: number;
    expertise: number;
    geoTarget: number;
    industry: number;
    jobTitle: number;
    language: number;
    location: number;
    mediaOutlet: number;
    productTypes: number;
    savedSearch: boolean;
    targetAudiences: number;
    totalNumberOfFieldsUsed: number;
  };
}

interface AudienceBuilderClickedAddToContactList {
  event: 'Audience Builder: Add to contact list' | 'Audience Builder: Show limit reached modal';
  traits: {
    from: 'list' | 'contact';
  };
}

interface AudienceBuilderClickedOpenContactInfo {
  event: 'Audience Builder: Clicked Open Contact Info';
  traits: {
    articleFeedFound: boolean;
    twitterFeedFound: boolean;
  };
}

interface ContactsImportLimitReachedEvent {
  event: 'Contacts: Import limit reached';
  traits: {
    imported?: number | null;
    max?: number | null;
  };
}

interface ContactsContactDetailsViewMediaHit {
  event:
    | 'Contacts: Clicked Media coverage button'
    | 'Contacts: Clicked Read article button'
    | 'Contacts: Clicked material title'
    | 'Contacts: Clicked article text';
  traits: {
    articleUrl: string;
    materialType?: string;
    materialId?: string;
  };
}

export interface ConfirmationPageAction {
  event: 'Confirmation page: Clicked action card';
  traits: {
    action: 'Go to dashboard' | 'Material preview' | 'View in newsroom';
  };
}
interface ContactsViewEvents {
  event: 'Contacts: View Contacts' | 'Contacts: View Labels';
  traits: {
    search: string;
    page: number;
    sort: NetworkContactListSort;
  };
}

interface ContactsViewContactEvent {
  event: 'Contacts: View Contact';
  traits: {
    contactId: string;
  };
}

interface ContactsCreatedContactEvent {
  event: 'Contacts: Created Contact';
  traits: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface ContactsUpdatedContactEvent {
  event: 'Contacts: Updated Contact';
  traits: {
    contactId: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface ContactsCreatedLabelEvent {
  event: 'Contacts: Created Label';
  traits: {
    labelName: string;
  };
}

interface ContactsUpdatedLabelEvent {
  event: 'Contacts: Updated Label';
  traits: {
    contactListId: string;
    contactListName: string;
  };
}

interface ContactsInactiveEvents {
  event:
    | 'Contacts: Seen View Inactive Contacts Button'
    | 'Contacts: Clicked to View Inactive Contacts'
    | 'Contacts: Clicked on Delete Inactive Contacts button'
    | 'Contacts: Clicked on Accept Delete Inactive Contacts button'
    | 'Contacts: Clicked on Cancel Delete Inactive Contacts button'
    | 'Contacts: Clicked on Continue Deleting Inactive Contacts button'
    | 'Contacts: Clicked on Go To Contact Lists button';
  traits?: {
    selectedContacts?: Selected;
    selectedContactsCount?: number;
    totalContactsCount?: number;
  };
}

interface ContactsInactiveCheckedEvents {
  event: 'Contacts: Checked Inactive Contact' | 'Contacts: Checked All Inactive Contacts';
  traits: {
    checked: boolean;
  };
}

interface DashboardLinkClicked {
  event:
    | 'Dashboard: Clicked Link in Create Newsroom Widget'
    | 'Dashboard: Clicked Link in Help&Support Widget';
  traits: {
    link: string;
    linkText: string;
  };
}

interface DataTableSortEvents {
  event: 'DataTable: Clicked Sort';
  traits: {
    name: string;
    sortLabel: string;
    sortOrder: 'asc' | 'desc';
  };
}
interface DataTableItemEvents {
  event: 'DataTable: Clicked item';
  traits: {
    name: string;
    itemLabel: string;
  };
}
interface DataTableEvents {
  event: 'DataTable: Clicked "Show more"';
  traits: {
    name: string;
  };
}

interface DemoAndTrialEvents {
  event: 'Demo' | 'Trial';
  traits: {
    name: string;
  };
}

interface WriteFirstPressreleaseEvent {
  event: 'Dashboard: Clicked Button in Write Pressrelease Widget';
  traits: {
    link: string;
    linkText: string;
  };
}

interface HelpPanelClickedArticleEvent {
  event: 'Help Panel: Clicked Article';
  traits: {
    article: string;
  };
}

interface HelpPanelClickedHelpCenterEvent {
  event: 'Help Panel: Clicked Help Center';
  traits: {
    destination: string;
  };
}

interface LatestStoryClickedMetricEvent {
  event: 'Latest Story: Clicked Metric';
  traits: {
    metric: string;
  };
}

interface MessagesSentExclusiveEvent {
  event: 'Messages: Sent Exclusive';
  traits: {
    attachments: number;
    messageLength: number;
    openedFrom: string;
    recipientsContacts: number;
    recipientsFromFilter: boolean;
    recipientsLists: number;
    sender: string;
  };
}

export interface MonitorOpenedChartPreviewModalEvent {
  event: 'Monitor: Opened Chart Preview Modal';
  traits: {
    chart: 'Profile Breakdown' | 'Hits Over Time' | 'Geographical Impact';
  };
}

interface MonitorClickedSaveNewSearchProfile {
  event: 'Monitor: Clicked Save New Search Profile';
  traits: {
    searchProfileId: string;
    isBooleanSearch: boolean;
  };
}
interface MonitorUpdatedSearchProfile {
  event: 'Monitor: Updated Search Profile';
  traits: {
    isBooleanSearch: boolean;
  };
}

interface MonitorStartPageEvent {
  event: 'Monitor: Start page - Click Get started';
  traits: {
    pressroomId: string;
    isAdmin: boolean;
    hasMonitor: boolean;
  };
}
interface MonitorCreateSmartSearchProfileEvent {
  event: 'Monitor: Create Smart Search Profile - View step';
  traits: {
    pressroomId: string;
    isAdmin: boolean;
    step: string;
    isOnboarding: boolean;
  };
}

interface MonitorActivateSmartSearchProfile {
  event: 'Monitor: Activate Smart Search Profile';
  traits: {
    pressroomId: string;
    isAdmin: boolean;
  };
}

interface MonitorActionAfterSmartSearchOnboarding {
  event: 'Monitor: Action after Smart Search Onboarding';
  traits: {
    pressroomId: string;
    action: string;
  };
}

interface PageEvent {
  event: 'Page';
  traits: {
    path: string;
    applicationLanguage: string;
  };
}

interface PaymentViewCheckoutEvent {
  event: 'Payment: View Checkout';
  traits: {
    isSalesDriven: boolean;
  };
}

interface PressReleaseClickedBackdateOrPublishOrScheduleEvent {
  event: 'Press Release: Clicked Backdate/Publish/Schedule';
  traits: {
    changedBoilerplate: boolean;
    publishType: 'now' | 'backdated' | 'scheduled';
    daysFromNow: number;
    includesMainImage: boolean;
    includesSnippet: boolean;
    numberOfContactLists: number;
    numberOfLinks: number;
    numberOfRegions: number;
    numberOfRelatedContacts: number;
    numberOfRelatedContent: number;
    numberOfRelatedMedia: number;
    numberOfSocialChannels: number;
    numberOfTags: number;
    numberOfTopics: number;
    numberOfRecommendedContacts: number;
    numberOfRecommendedContactsFromAudienceBuilder: number;
    numberOfRecommendedContactsFromAudienceTargeting: number;
    numberOfRecommendedContactsFromFindContacts: number;
    published?: string;
    visibility?: string;
    withoutSendingEmails: boolean;
    generatedByAiWriter: boolean;
    template?: TemplateTypes;
  };
}

interface StoryMaterialClickedBackdateOrPublishOrScheduleEvent {
  event: 'News: Clicked Backdate/Publish/Schedule' | 'Blog Post: Clicked Backdate/Publish/Schedule';
  traits: {
    publishType: 'now' | 'backdated' | 'scheduled';
    daysFromNow: number;
    generatedByAiWriter: boolean;
    template?: TemplateTypes;
    numberOfRecommendedContacts: number;
    numberOfRecommendedContactsFromAudienceBuilder: number;
    numberOfRecommendedContactsFromAudienceTargeting: number;
    numberOfRecommendedContactsFromFindContacts: number;
  };
}

interface EventMaterialClickedBackdateOrPublishOrScheduleEvent {
  event: 'Event: Clicked Backdate/Publish/Schedule';
  traits: {
    publishType: 'now' | 'backdated' | 'scheduled';
    daysFromNow: number;
  };
}

interface PublishArchivedMaterialEvent {
  event: 'Publish: Archived Material';
  traits: {
    materialType: MaterialType;
  };
}

export interface PublishClickedOnDisabledPaidFeatureEvent {
  event: 'Publish: Clicked on Disabled Paid Feature';
  traits: {
    step:
      | 'Header Image'
      | 'Preview'
      | 'Publish Time'
      | 'Related Contacts'
      | 'Related Content'
      | 'Related Media'
      | 'Social Channels'
      | 'Visibility';
  };
}

export interface PublishClickedOnMissedFeaturesBanner {
  event: 'Publish: Clicked on Missed Features Banner';
  traits: {
    step: 'Edit' | 'Distribution';
  };
}

interface PublishClickedOnOpenedEmailsRateGraphTime {
  event: 'Publish: Clicked on Opened Emails Rate Graph Time';
  traits: {
    time: string;
  };
}
interface PublishClickedOnOpenedEmailsRateGraphDay {
  event: 'Publish: Clicked on Opened Emails Rate Graph Day';
  traits: {
    day: string;
  };
}

interface PublishSaveMaterial {
  event: 'Publish: Save material';
  traits: {
    materialId: string;
    materialType: MaterialType;
    header: string;
    summary: string;
    body: string;
    suggestions?: {
      header?: string[];
      summary?: string;
    };
  };
}

interface PublishClickedOnTipsButtonSidebar {
  event:
    | 'Publish: Clicked on Tips Button Sidebar'
    | 'Publish: Seen Tips Button Sidebar'
    | 'Publish: Clicked on Regenerate Button in Sidebar'
    | 'Publish: Clicked on Generate Button in Sidebar';
  traits: {
    item: 'header' | 'body' | 'summary' | 'socialMedia';
  };
}

interface PublishToggleExpandSidebar {
  event: 'Publish: Toggle Expand Sidebar';
  traits: {
    expand: boolean;
  };
}
interface PublishToggleExpandItemInSidebar {
  event: 'Publish: Toggle Expand Item in Sidebar';
  traits: {
    expand: boolean;
    item: 'header' | 'body' | 'summary' | 'socialMedia';
    suggestions?: string | string[] | SuggestionType[];
  };
}
interface PublishClickedOnFeedbackInSidebar {
  event: 'Publish: Clicked on Feedback in Sidebar';
  traits: {
    positive?: boolean;
    comment?: string;
  };
}
interface PublishClickedOnSuggestionInSidebar {
  event: 'Publish: Clicked on Suggestion in Sidebar' | 'Publish: Clicked on Undo Suggestion';
  traits: {
    item: 'header' | 'summary' | 'socialMedia';
    suggestion: string | SuggestionType;
    previousText?: string;
  };
}
interface PublishFetchedSuggestionsInSidebar {
  event: 'Publish: Fetched Suggestions in Sidebar' | 'Publish: Fetch Suggestions in Sidebar Failed';
  traits: {
    item: 'header' | 'summary' | 'socialMedia';
    suggestions?: string | string[] | SuggestionType[];
  };
}

interface PublishClickedOnClone {
  event:
    | 'Publish: Clicked on Clone in Dropdown'
    | 'Publish: Clicked on Cancel in Clone Modal'
    | 'Publish: Clicked on Clone in Clone Modal';
  traits: {
    materialId: string;
    materialType: string;
    targetPressroomIds?: string[];
  };
}

interface PublishClickedOnDuplicate {
  event:
    | 'Publish: Duplicated material successfully'
    | 'Publish: Clicked on Duplicate in Dropdown'
    | 'Publish: Clicked on Cancel in Duplicate Modal'
    | 'Publish: Clicked on Duplicate in Duplicate Modal';
  traits: {
    materialId: string;
    materialType: string;
  };
}

interface PublishClickedOnScheduleSendExclusive {
  event:
    | 'Publish: Clicked on Schedule Send Exclusive dropdown'
    | 'Publish: Clicked on Schedule Send Exclusive button';
  traits: {
    materialId: string;
    materialType: string;
    edit: boolean;
  };
}

interface PublishFirstBodyTextEnteredEvent {
  event: 'Publish: First body text entered word count';
  traits: {
    materialType: string;
    wordCount: number;
  };
}

interface UnlockerViewEvent {
  event: 'Unlocker: View';
  traits: {
    upsell: string;
  };
}

interface CreatingBrandReportEvent {
  event: 'Brand Reports: Creating Report';
  traits: {
    currentStep: string;
  };
}

interface BrandReportDashboardBannerEvent {
  event: 'Brand Report Dashboard Banner: CTA Clicked';
  traits: {
    type: string;
  };
}

type InAppMessageType = 'Popup' | 'Sticky' | 'Modal';

interface InAppMessageMessageEvent {
  event: 'In-app message: Seen message' | 'In-app message: Closed message';
  traits: {
    message_id: Notice;
    message_title: string;
    message_type: InAppMessageType;
  };
}

interface InAppMessageActionEvent {
  event:
    | 'In-app message: Clicked button'
    | 'In-app message: Clicked link'
    | 'In-app message: Clicked primary CTA';
  traits: {
    message_id: Notice;
    message_title: string;
    message_type: InAppMessageType;
    link_title: string;
    link_url?: string;
  };
}

interface NPS {
  event: 'NPS: Seen' | 'NPS: Clicked Score' | 'NPS: Closed';
  traits: {
    message_type: InAppMessageType;
    nps_score?: number;
  };
}

interface MenuNavigation {
  event:
    | 'Menu Navigation - Clicked'
    | 'Sub Menu Navigation - Clicked'
    | 'Dropdown Menu Navigation - Clicked';

  traits: {
    navigated_to: string;
  };
}

interface CoverageReportsTrialEvents {
  event: 'Coverage Reports: Clicked Know More' | 'Coverage Reports: Clicked Add to Subscription';
  traits: {
    trial_days_left: number;
  };
}

interface CoverageReportsPopupClosed {
  event: 'Coverage Reports: Closed Popup';
  traits: {
    trial_days_left: number;
    popup_kind:
      | 'popover'
      | 'buy_modal'
      | 'demo_confirmation'
      | 'purchase_confirmation'
      | 'trial_ended'
      | 'access_lost';
  };
}

interface CoverageReportsSort {
  event: 'Coverage Reports: Sorted Coverage';
  traits: {
    coverage_id: string;
    index_before: number;
    index_after: number;
  };
}

interface TrialPillarEvents {
  event: 'Header - Open Trial pill' | 'Trial Menu - CTA Clicked' | 'Trial Modal - form submitted';
  traits: {
    pill_text?: string;
    cta_text?: string;
  };
}

interface UpsellPlanEvents {
  event: 'Upsell plan Modal - form submitted';
  traits: {
    cta_text?: string;
  };
}

interface PitchClickEdit {
  event: 'Pitch: Click edit';
  traits: {
    pitchId: string;
  };
}

interface PitchClickCreate {
  event: 'Pitch: Click create';
  traits: {
    from: 'ContactDetails' | 'MaterialListActionMenu' | 'PitchList';
    contactId?: string;
    materialId?: string;
    materialType?: string;
  };
}

interface PitchClickOpenSelectJournalist {
  event: 'Pitch: Click open SelectJournalist';
  traits: {
    by: 'to-input' | 'sidebar-recipient-button';
    pitchId?: string;
  };
}

interface PitchChangeTo {
  event: 'Pitch: Change To';
  traits: {
    pitchId?: string;
    to: PitchToFragmentFragment;
    previousTo?: PitchToFragmentFragment;
  };
}

interface PitchSearchJournalist {
  event: 'Pitch: Search journalist';
  traits: {
    pitchId?: string;
    searchTerm: string;
  };
}

interface PitchAddAttachment {
  event: 'Pitch: Add attachment';
  traits: {
    pitchId?: string;
    materialType: MaterialType;
    materialId: string;
  };
}

interface PitchClickPreview {
  event: 'Pitch: Click preview';
  traits: {
    pitchId: string;
  };
}

interface PitchClickOpenInEmail {
  event: 'Pitch: Click open in email';
  traits: {
    pitchId?: string;
    from: 'PitchList' | 'PitchEdit';
  };
}

interface PitchClickDelete {
  event: 'Pitch: Click delete';
  traits: {
    pitchId: string;
    from: 'PitchList' | 'PitchEdit';
  };
}

interface PitchClickRelevantArticle {
  event: 'Pitch: Click relevant article';
  traits: {
    pitchId?: string;
    journalistId: string;
    articleId: string;
  };
}

interface PitchClickLatestArticle {
  event: 'Pitch: Click latest article';
  traits: {
    pitchId?: string;
    journalistId: string;
    articleId: string;
  };
}

interface PitchCloseSidebar {
  event: 'Pitch: Close sidebar';
  traits: {
    pitchId?: string;
  };
}
interface PitchSetFromAddress {
  event: 'Pitch: Set from address';
  traits: {
    pitchId?: string;
    from: string;
  };
}

interface PitchSetReplyToAddress {
  event: 'Pitch: Set replyTo address';
  traits: {
    pitchId?: string;
    replyTo: string;
  };
}

interface PitchAboutJournalistError {
  event: 'Pitch: About journalist error';
  traits: {
    pitchId?: string;
  };
}

interface PitchAboutJournalistFlagged {
  event: 'Pitch: About journalist flagged';
  traits: {
    pitchId?: string;
  };
}
interface PitchClickOpenDNSModal {
  event: 'Pitch: Click open DNS modal';
  traits: {
    pitchId?: string;
  };
}

interface PitchClickGoToEmailSettings {
  event: 'Pitch: Click go to email settings';
  traits: {
    pitchId?: string;
  };
}

interface PitchClickOpenSendModal {
  event: 'Pitch: Click open send modal';
  traits: {
    pitchId?: string;
  };
}

interface PitchClickSend {
  event: 'Pitch: Click send';
  traits: {
    pitchId?: string;
  };
}

interface PitchClickMediaHitArticleUrl {
  event: 'Pitch: Click media hit article url';
  traits: {
    pitchId: string;
    articleUrl: string;
  };
}

type SeenGuideBannerTraits = {
  componentId: string;
  labelText: string;
  componentHeadline: string;
};

type ClickedLinkInGuideBannerTraits = SeenGuideBannerTraits & {
  linkText: string;
  linkUrl: string;
};
interface GuideBanner {
  event:
    | 'Guide Banner: Seen banner'
    | 'Guide Banner: Clicked link in banner'
    | 'Guide Banner: Dismissed banner';
  traits: SeenGuideBannerTraits | ClickedLinkInGuideBannerTraits;
}

interface OnboardingWidget {
  event:
    | 'Onboarding Widget: Widget seen'
    | 'Onboarding Widget: Widget opened'
    | 'Onboarding Widget: Checklist item clicked';
  traits?: {
    completed: boolean;
    widgetItem: string;
  };
}

interface OnboardingHero {
  event: 'Onboarding Hero: Hero seen' | 'Onboarding Hero: Hero item clicked';
  traits?: {
    widgetItem: string;
  };
}

export interface InAppPurchase {
  event:
    | 'In-app purchase: Triggered'
    | 'In-app purchase: Product details'
    | 'In-app purchase: Payment'
    | 'In-app purchase: Confirmation';
  traits: {
    step?: number;
    product: 'email-credits';
    productDetails?: string;
    newProductDetails?: string;
    location?: string;
  };
}

interface EmailCredits {
  event: 'Email Credits: limit exceeded';
  traits: {
    currentLimit: number;
  };
}

interface NewsroomSetup {
  event:
    | 'Newsroom Setup: Branding'
    | 'Newsroom Setup: Press Contact'
    | 'Newsroom Setup: Company Description'
    | 'Newsroom Setup: Company Info'
    | 'Newsroom Setup: Go Live'
    | 'Newsroom Setup: Go Live - Proceed'
    | 'Newsroom Setup: Branding - Proceed'
    | 'Newsroom Setup: Press Contact - Proceed'
    | 'Newsroom Setup: Company Description - Proceed'
    | 'Newsroom Setup: Company Info - Proceed'
    | 'Newsroom Setup: Clicked Not ready yet';

  traits: {
    step?: number;
    prepopulated?: boolean;
    dataChanged?: boolean;
    fieldTitlePopulated?: boolean;
    fieldEmailPopulated?: boolean;
    fieldNumberPopulated?: boolean;
    fieldPhotoPopulated?: boolean;
    numberOfLivePublications?: number;
  };
}

interface MultipleBoilerplates {
  event:
    | 'Settings: Create Boilerplate'
    | 'Settings: Delete Boilerplate'
    | 'Publish: Select Boilerplate Template';
  traits: {
    boilerplateName: string;
  };
}

interface CopilotIntroModalEvent {
  event: 'Copilot Intro Modal: Clicked next';
  traits: {
    step: number;
  };
}

interface SendExclusiveEvents {
  event:
    | 'Send Exclusive: Clicked at Send Exclusive button'
    | 'Send Exclusive: Delete Scheduled Send Exclusive'
    | 'Send Exclusive: Added/Removed Contact from Select Recipients'
    | 'Send Exclusive: Changed Scheduled Date'
    | 'Send Exclusive: Clicked on Add Content button'
    | 'Send Exclusive: Clicked on Add Media button'
    | 'Send Exclusive: Clicked on Send Exclusive button in publish'
    | 'Send Exclusive: Clicked on Send Exclusive button on Contact page'
    | 'Send Exclusive: Clicked on Send Exclusive button on Contact List page';
  traits?: {
    materialType?: MaterialType;
  };
}

interface CreateNewMaterial {
  event:
    | 'Clicked create new publication button'
    | 'Clicked generate new publication with ai button';
  traits: {
    materialType: UrlRegularPublishMaterialTypes;
  };
}

interface Navigation {
  event: 'Navigation link: clicked';
  traits: {
    menuCategory: string;
    linkText: string;
  };
}

interface MonitorCreatedUpdatedAlertEvent {
  event: 'Monitor: Created Alert' | 'Monitor: Updated Alert';
  traits: {
    alertId: string;
    alertName: string;
    isScheduled: boolean;
    isRealTime: boolean;
    numReceipients: number;
    numSearchProfiles: number;
    filteredMediaTypes?: string[];
    filteredCountries?: string[]; // Have a separate field for countries for more convenient filtering
    filteredCountriesAndRegions?: string[];
    filteredLanguages?: string[];
    filteredSentiments?: string[];
    filteredRelevanceLevels?: number[];
    filteredExcludedSourceGroups?: string[];
    filteredIncludedSourceGroups?: string[];
    filteredSocialMediaHandlesExcluded?: string[];
  };
}

export type TrackInput =
  | AiWriterBaseEvent
  | AiWriterClickedBuyCreditsEvent
  | AiWriterClickedCreditsEvent
  | AiWriterGenerateEvent
  | AiWriterQuestionsEvent
  | AiWriterUseCreditsEvent
  | AnalyzeExportEvent
  | AnalyzeMediaCoverageLinkEvent
  | AnalyzeTimeFilterEvent
  | AnalyzeYourContentClickCopyLinkEvent
  | AudienceBuilderClickedAddToContactList
  | AudienceBuilderClickedKeywordSearch
  | AudienceBuilderClickedOpenContactInfo
  | AudienceBuilderClickedSaveSearchEvent
  | AudienceBuilderClickedSearchEvent
  | ConfirmationPageAction
  | ContactsImportLimitReachedEvent
  | ContactsContactDetailsViewMediaHit
  | ContactsCreatedContactEvent
  | ContactsCreatedLabelEvent
  | ContactsInactiveCheckedEvents
  | ContactsInactiveEvents
  | ContactsUpdatedContactEvent
  | ContactsUpdatedLabelEvent
  | ContactsViewContactEvent
  | ContactsViewEvents
  | CopilotIntroModalEvent
  | CoverageReportEditSectionDescriptionEvent
  | CoverageReportOnboardingEvent
  | CoverageReportPrAssistantTrigger
  | CoverageReportRemoveHighlightEvent
  | CoverageReportsPopupClosed
  | CoverageReportsSort
  | CoverageReportsTrialEvents
  | CreatingBrandReportEvent
  | DashboardLinkClicked
  | DataTableEvents
  | DataTableItemEvents
  | DataTableSortEvents
  | DemoAndTrialEvents
  | EmailCredits
  | EventMaterialClickedBackdateOrPublishOrScheduleEvent
  | GuideBanner
  | HelpPanelClickedArticleEvent
  | HelpPanelClickedHelpCenterEvent
  | InAppMessageActionEvent
  | InAppMessageMessageEvent
  | InAppPurchase
  | LatestStoryClickedMetricEvent
  | MenuNavigation
  | MessagesSentExclusiveEvent
  | MonitorClickedSaveNewSearchProfile
  | MonitorOpenedChartPreviewModalEvent
  | MonitorUpdatedSearchProfile
  | MonitorStartPageEvent
  | MonitorActivateSmartSearchProfile
  | MonitorActionAfterSmartSearchOnboarding
  | MonitorCreatedUpdatedAlertEvent
  | MonitorCreateSmartSearchProfileEvent
  | MultipleBoilerplates
  | Navigation
  | NewsroomSetup
  | NPS
  | OnboardingHero
  | OnboardingWidget
  | OpenOnboaringVideoCourseModal
  | PageEvent
  | PaymentViewCheckoutEvent
  | PitchClickCreate
  | PitchClickEdit
  | PitchClickOpenSelectJournalist
  | PitchChangeTo
  | PitchSearchJournalist
  | PitchAddAttachment
  | PitchClickPreview
  | PitchClickOpenInEmail
  | PitchClickDelete
  | PitchClickRelevantArticle
  | PitchClickLatestArticle
  | PitchCloseSidebar
  | PitchSetFromAddress
  | PitchSetReplyToAddress
  | PitchAboutJournalistError
  | PitchAboutJournalistFlagged
  | PitchClickOpenSendModal
  | PitchClickSend
  | PitchClickMediaHitArticleUrl
  | PitchClickOpenDNSModal
  | PitchClickGoToEmailSettings
  | PressReleaseClickedBackdateOrPublishOrScheduleEvent
  | PublishArchivedMaterialEvent
  | PublishClickedOnDisabledPaidFeatureEvent
  | PublishClickedOnFeedbackInSidebar
  | PublishClickedOnMissedFeaturesBanner
  | PublishClickedOnOpenedEmailsRateGraphDay
  | PublishClickedOnOpenedEmailsRateGraphTime
  | PublishClickedOnClone
  | PublishClickedOnDuplicate
  | PublishClickedOnScheduleSendExclusive
  | PublishClickedOnSuggestionInSidebar
  | PublishClickedOnTipsButtonSidebar
  | PublishFetchedSuggestionsInSidebar
  | PublishRecommendedJournalistsEvent
  | PublishSaveMaterial
  | PublishToggleExpandItemInSidebar
  | PublishToggleExpandSidebar
  | PublishFirstBodyTextEnteredEvent
  | SendExclusiveEvents
  | SocialMediaClickedSuggestedTime
  | SocialMediaCreatedPost
  | SocialMediaDeletePost
  | SocialMediaEditedPost
  | SocialMediaEditPostOpened
  | StoryMaterialClickedBackdateOrPublishOrScheduleEvent
  | TopicsAndRegionsEvent
  | ToolTrainingVideosInteractions
  | TraitsLessEvent
  | TrialPillarEvents
  | UpsellPlanEvents
  | UnlockerViewEvent
  | WriteFirstPressreleaseEvent
  | ReportsSharedTrackingEvent
  | BrandReportDashboardBannerEvent
  | MediaRelationsPaginationEvent
  | MediaRelationsListSortEvent
  | MediaRelationsOutletListSortEvent
  | MediaRelationsSelectEvent
  | MediaRelationsSelectOutletEvent
  | MediaRelationsImportEvent
  | MediaRelationsFiltersEvent
  | MediaRelationsOutletFiltersEvent
  | MediaRelationsSearchModeToggleEvent
  | CreateNewMaterial
  | OnboardingIntroNavigateToCompetitors
  | OnboardingIntroDone;

declare global {
  interface Window {
    analyticsSuperTraits?: {
      companySize: CompanySize | undefined;
      customerId: string | undefined;
      customerOrigin: string | undefined;
      liveNewsroom: boolean | undefined;
      locale: UserLocale | undefined;
      persona: string | undefined;
      plan: string | undefined;
      pressroomId: string | undefined;
      sourceId: string | undefined;
      tier: Tier | undefined;
      trial: boolean | undefined;
      type: string | undefined;
      userId: string | undefined;
      userEmail: string | undefined;
    } & {
      [key in `betaFeature_${keyof Omit<BetaFeaturesFragment, 'id' | '__typename'>}`]?: boolean;
    } & {
      [key in `betaFeature_${keyof Omit<CopilotFeaturesFragment, 'id' | '__typename'>}`]?: boolean;
    };
  }
}

export const track = initTracking<
  TrackInput,
  IdentifyEvent & {
    traits: {
      email: string;
      name: string;
      username: string;
    };
  },
  NativePageEvent & {
    traits: {
      applicationLanguage: string;
    };
  }
>({
  application: 'publish',
  superTraits: () => window.analyticsSuperTraits,
  writeKey:
    import.meta.env.VITE_SEGMENT_WRITE_KEY ||
    (window.location.hostname.endsWith('.mynewsdesk.com') &&
    (import.meta.env.VITE_BUILD_VERSION as string)?.startsWith('production')
      ? 'lOBnxM6VvaEbs4ERZlig28Rm4LZGD1WN'
      : undefined),
});
